import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { DashboardMockup } from '../components/landing/DashboardMockup';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import {
  Wrench,
  Shield,
  Users,
  Phone,
  ArrowRight,
  CheckCircle,
  Building2,
  ClipboardList,
  HeadphonesIcon,
  PoundSterling,
  Bell,
  Receipt,
  FileText,
  Calculator,
  MessageSquare,
  TrendingUp,
  FileSignature,
  Send,
  Car,
} from 'lucide-react';

const services = [
  {
    icon: Building2,
    title: 'Property Dashboard',
    description: 'Manage your residential properties, tenants, and documents from a single dashboard.'
  },
  {
    icon: Wrench,
    title: 'Issue Tracking',
    description: 'Log, track, and resolve maintenance issues with full status history and tenant visibility.'
  },
  {
    icon: PoundSterling,
    title: 'Finances & Tax Reporting',
    description: 'SA105 and CT600-categorised cashflow, rent collection, CSV export, and 3-month rolling forecasts — for individual and company landlords.'
  },
  {
    icon: HeadphonesIcon,
    title: 'Document Tracker',
    description: "Property MOT checks, written tenancy statements, document delivery, and Renters' Rights Act document checklist."
  }
];

const features = [
  {
    icon: ClipboardList,
    title: 'Issue Tracking',
    description: 'Report and track maintenance issues in real-time'
  },
  {
    icon: TrendingUp,
    title: 'Finances & Tax',
    description: 'SA105 / CT600 cashflow analytics, category breakdown, and CSV export'
  },
  {
    icon: Car,
    title: 'Property MOT',
    description: 'Property MOT overview across your entire portfolio'
  },
  {
    icon: FileSignature,
    title: 'Written Statements',
    description: 'Generate and send legally-structured tenancy agreements'
  },
  {
    icon: MessageSquare,
    title: 'Message Center',
    description: 'Direct landlord–tenant messaging within the portal'
  },
  {
    icon: Send,
    title: 'Document Delivery',
    description: 'Send How to Rent guides and legal docs — no account needed'
  },
  {
    icon: Bell,
    title: 'Rent Reminders',
    description: 'Automated email reminders before rent is due'
  },
  {
    icon: Receipt,
    title: 'Digital Receipts',
    description: 'Instant payment receipts for every transaction'
  },
];

const benefits = [
  'Self-service property dashboard',
  'Automated rent tracking & reminders',
  'Transparent issue tracking',
  'Digital payment receipts',
  'Mobile-friendly portal',
  'Clear communication'
];

const portalChecklist = [
  'Report maintenance issues and track repairs',
  'Message your landlord directly in the portal',
  'View your written tenancy statement',
  'Receive required documents digitally',
  'Stay informed with automated notifications'
];

const mtdTimeline = [
  { date: '6 Apr 2026', label: 'MTD mandatory (income > £50k)', color: 'bg-red-500' },
  { date: '6 Apr 2027', label: 'Threshold drops to > £30k', color: 'bg-amber-500' },
  { date: '6 Apr 2028', label: 'Threshold drops to > £20k', color: 'bg-amber-400' },
];

const wsFeatures = [
  { icon: FileSignature, label: 'Written tenancy statements', sub: 'Clause library, PDF generation, signature lifecycle' },
  { icon: Send, label: 'Document delivery', sub: 'How to Rent Guide, RRA Info Sheet, 12 document types' },
  { icon: Car, label: 'Property MOT', sub: 'Property MOT overview across your full portfolio' },
];

export const Home = () => {
  const { isAuthenticated, isTenant, isLandlord, isAdmin } = useAuth();
  const { publicDocumentDispatchEnabled } = useSiteSettings();
  const canAccessSendDocuments = publicDocumentDispatchEnabled || (isAuthenticated() && (isLandlord() || isAdmin()));
  useEffect(() => { document.title = 'SIYAK - Property Management for Independent Landlords'; }, []);

  return (
    <Layout>
      <section className="relative bg-navy-900 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 to-navy-800" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block mb-4 px-3 py-1 rounded-full bg-royal-100 text-royal-700 text-xs font-semibold tracking-wide">
                Free to use · No subscription required
              </span>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Property management tools built for independent landlords
              </h1>
              <p className="text-lg text-navy-200 mb-8">
                Free to use — no card required. Manage tenants, track rent, and stay compliant with the Renters' Rights Act, all in one place.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                {isAuthenticated() && isTenant() ? (
                  <div className="flex flex-col items-center sm:items-start">
                    <Button
                      size="lg"
                      className="bg-royal-600 hover:bg-royal-700"
                      asChild
                      data-testid="report-issue-btn"
                    >
                      <Link to="/report-issue">
                        <Wrench className="mr-2 h-5 w-5" />
                        Report an Issue
                      </Link>
                    </Button>
                    <p className="mt-2 text-xs text-navy-300 text-center">
                      Simple setup. No commitment.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-start">
                    <Button
                      size="lg"
                      className="bg-royal-600 hover:bg-royal-700"
                      asChild
                      data-testid="tenant-login-btn"
                    >
                      <Link to="/login">
                        Portal Access
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                    <p className="mt-2 text-xs text-navy-300">
                      Simple setup. No commitment.
                    </p>
                  </div>
                )}
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  asChild
                  data-testid="contact-btn"
                >
                  <Link to="/contact">
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Us
                  </Link>
                </Button>
              </div>
              <p className="mt-4 text-xs text-navy-300">
                SIYAK helps you track and manage property information. It does not provide legal advice.
              </p>
            </div>

            <div className="w-full">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              Our Services
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Complete property management solutions for landlords and a smooth
              portal experience for all residents.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card key={service.title} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-royal-100 mb-4">
                    <service.icon className="h-7 w-7 text-royal-600" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-navy-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-navy-900" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Platform Features
            </h2>
            <p className="text-navy-200 max-w-2xl mx-auto">
              Modern tools to make property management simple and efficient
            </p>
          </div>

          <div className="grid max-w-6xl mx-auto grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="h-full rounded-2xl border border-white/10 bg-white/5 text-center p-4 md:p-5"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-royal-600/20 mb-3">
                  <feature.icon className="h-6 w-6 text-royal-400" />
                </div>
                <h3 className="font-medium text-white text-sm mb-1">
                  {feature.title}
                </h3>
                <p className="text-xs text-navy-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-navy-50" data-testid="tenant-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                Need Portal Access?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Your online portal makes it easy to report maintenance issues, track updates,
                and communicate with your landlord.
              </p>
              <ul className="space-y-3 mb-8">
                {portalChecklist.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-navy-700">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild data-testid="register-tenant-btn">
                  <Link to="/register">
                    Create Account
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/login">
                    Sign In
                  </Link>
                </Button>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <ClipboardList className="h-8 w-8 text-royal-600" />
                <h3 className="font-heading font-semibold text-xl">Issue Tracking</h3>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                  <div className="font-medium text-green-800">Resolved</div>
                  <div className="text-sm text-green-600">Boiler repair completed</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                  <div className="font-medium text-blue-800">In Progress</div>
                  <div className="text-sm text-blue-600">Window seal replacement</div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                  <div className="font-medium text-yellow-800">Reported</div>
                  <div className="text-sm text-yellow-600">Bathroom tap leak</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="health-check-cta" className="py-16 md:py-24 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-2">
                Renters' Rights Act 2025
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                Are you ready for the changes?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Answer up to 18 questions across 5 topics and find out where you stand as a landlord — no account needed.
              </p>
              <Button asChild className="bg-green-600 hover:bg-green-700">
                <Link to="/landlord-health-check">
                  Check your readiness →
                </Link>
              </Button>
              <p className="text-sm text-muted-foreground mt-3">Takes about 5 minutes</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg border border-green-100">
              <div className="flex items-center gap-3 text-navy-700 text-sm py-2 border-b border-green-100 last:border-0">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>EPC Certificate in date</span>
              </div>
              <div className="flex items-center gap-3 text-navy-700 text-sm py-2 border-b border-green-100 last:border-0">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>Gas Safety Certificate current</span>
              </div>
              <div className="flex items-center gap-3 text-navy-700 text-sm py-2 border-b border-green-100 last:border-0">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>Deposit protection registered</span>
              </div>
              <div className="flex items-center gap-3 text-navy-700 text-sm py-2 border-b border-green-100 last:border-0">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>Fixed terms removed</span>
              </div>
              <div className="flex items-center gap-3 text-navy-700 text-sm py-2 border-b border-green-100 last:border-0">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>Section 13 process in place</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-2">
                Making Tax Digital · 6 April 2026
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                Are you ready for MTD?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Making Tax Digital for Income Tax affects landlords from April 2026. Find out
                if you're in scope, when your deadline is, and what you need to do.
              </p>
              <Button asChild className="bg-amber-500 hover:bg-amber-600 text-white">
                <Link to="/mtd-guide">
                  <Calculator className="mr-2 h-4 w-4" />
                  Read the MTD guide →
                </Link>
              </Button>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg border border-amber-100 space-y-3">
              {mtdTimeline.map((item) => (
                <div key={item.date} className="flex items-center gap-4 py-2 border-b border-amber-50 last:border-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.color}`} />
                  <span className="text-xs font-semibold text-muted-foreground w-24 flex-shrink-0">{item.date}</span>
                  <span className="text-sm text-navy-700">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="written-statements-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold text-royal-700 uppercase tracking-wide mb-2">
                Written Statements
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                Written Statements & Document Delivery
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Create written statements built from a clause library and generate
                a signed PDF — or send required documents like the How to Rent Guide
                {publicDocumentDispatchEnabled
                  ? ' to any tenant instantly, no account needed.'
                  : ' from your landlord account with tracked delivery records.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-royal-600 hover:bg-royal-700">
                  <Link to="/register">
                    Get started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                {canAccessSendDocuments ? (
                  <Button variant="outline" asChild>
                    <Link to="/send-documents">
                      <Send className="mr-2 h-4 w-4" />
                      Send documents now
                    </Link>
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="bg-navy-50 p-8 rounded-lg shadow-lg space-y-4">
              {wsFeatures.map(({ icon: Icon, label, sub }) => (
                <div key={label} className="flex items-start gap-4 p-4 bg-white rounded-lg border border-navy-100">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-royal-100 flex-shrink-0">
                    <Icon className="h-5 w-5 text-royal-600" />
                  </div>
                  <div>
                    <p className="font-medium text-navy-900 text-sm">{label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-2 text-navy-700">
                    <CheckCircle className="h-4 w-4 text-royal-600 flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy-900 mb-6">
                Why Choose SIYAK?
              </h2>
              <p className="text-muted-foreground text-lg mb-4">
                SIYAK gives you the tools to manage your properties professionally — Property MOT tracking, rent management, document delivery, and tenant communications in one place.
              </p>
              <p className="text-muted-foreground">
                Whether you're tracking a maintenance issue or preparing for the Renters' Rights Act, everything you need is in your dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-navy-900" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Have Questions?
          </h2>
          <p className="text-navy-200 mb-8">
            Have a question or suggestion? We'd love to hear from you.
          </p>
          <Button
            size="lg"
            className="bg-royal-600 hover:bg-royal-700"
            asChild
          >
            <Link to="/contact">
              <Phone className="mr-2 h-5 w-5" />
              Contact Us
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};
